import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Loader2, AtSign, Lock } from "lucide-react";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { useLang } from "@/i18n/LangContext";
import { LocalizedLink, useLocalizedNav } from "@/i18n/LocalizedLink";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function SignIn() {
  const { t } = useLang();
  const { login } = useUserAuth();
  const lnav = useLocalizedNav();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/profile";
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.identifier, form.password);
      toast.success(t("auth.welcome_back"));
      lnav(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[80vh] grid place-items-center p-6 relative" data-testid="signin-page">
      <div className="aurora opacity-50" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand to-brand-cyan glow-blue mb-4">
            <LogIn className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">{t("auth.welcome_back")}</h1>
          <p className="text-sm text-zinc-400">{t("auth.sign_in_sub")}</p>
        </div>

        <div className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 space-y-4">
          <GoogleAuthButton next={next} />

          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-600 font-bold">
            <div className="flex-1 h-px bg-white/5" />
            <span>{t("auth.or")}</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">
                <AtSign className="w-3 h-3 inline me-1" /> {t("auth.identifier")}
              </label>
              <input
                required value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none transition-colors"
                data-testid="signin-identifier"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2 block">
                <Lock className="w-3 h-3 inline me-1" /> {t("auth.password")}
              </label>
              <input
                required type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-[#080808] border border-white/10 rounded-lg px-4 py-3 focus:border-brand outline-none transition-colors"
                data-testid="signin-password"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-lg glow-blue transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="signin-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {t("nav.sign_in")}
            </button>
            <div className="flex items-center justify-between pt-2 text-sm">
              <LocalizedLink to="/forgot-password" className="text-brand-cyan hover:text-white" data-testid="forgot-link">{t("auth.forgot")}</LocalizedLink>
              <span className="text-zinc-500">
                {t("auth.no_account")} <LocalizedLink to="/signup" className="text-white font-semibold" data-testid="goto-signup-link">{t("nav.sign_up")}</LocalizedLink>
              </span>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
